import React, {
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Button,
  Dropdown,
  Icon,
  Modal,
  ToggleCheckbox,
} from "../components/tailwind";
import { Option } from "../components/tailwind/dropdown/Dropdown";
import {
  createTranslationMemorySegmentRegex,
  searchTranslationMemory,
} from "../api/translationMemoryApi";
import { debounce } from "lodash";
import { Spinner } from "../utils/Spinner";
import {
  NotificationAppearance,
  setDjangoToastOpen,
} from "../api/djangoToastSlice";
import { store } from "../utils/store";
import { useValidateRegexMutation } from "../api/CATViewSlice";
import { SegmentationItem } from "./types";
import {
  useCreateTaggingRuleMutation,
  useGetTagRulesQuery,
} from "../api/translationMemorySegmentTagRulesSlice";
import { NOOP } from "..";

type Props = {
  allowedToShowAdminControls: boolean;
  regexOptions: Option<number>[];
  setRegexId: (id: number) => void;
  regexId: number;
  addNewRegex: (regex: string, id: number, name?: string) => void;
  segmentationItems: SegmentationItem[];
  setSegmentationItems: (
    updates: { index: number; updates: Partial<SegmentationItem> }[]
  ) => void;
  sourceLanguage: string;
  targetLanguage: string;
  setDisabledTaggingRules: React.Dispatch<SetStateAction<number[]>>;
  disabledTaggingRules: number[];
};

export const TranslationViewAdminPanel: React.FC<Props> = ({
  allowedToShowAdminControls,
  regexOptions,
  setRegexId,
  regexId,
  addNewRegex,
  segmentationItems,
  setSegmentationItems,
  sourceLanguage,
  targetLanguage,
  disabledTaggingRules,
  setDisabledTaggingRules,
}) => {
  const [validateRegex] = useValidateRegexMutation();
  const {
    data: taggingRules,
    isLoading: isFetchingTagRules,
  } = useGetTagRulesQuery();
  const [createTaggingRule] = useCreateTaggingRuleMutation();
  const segmentationSettingsDialogRef = useRef<HTMLDialogElement>(null);
  const taggingRulesDialogRef = useRef<HTMLDialogElement>(null);
  const mountedRef = useRef<boolean>(false);
  const [newRegexName, setNewRegexName] = useState<string>("");
  const [newRegexRegex, setNewRegexRegex] = useState<string>("");
  const [regexError, setRegexError] = useState<string | null>(null);
  const [isCheckingRegex, setIsCheckingRegex] = useState<boolean>(false);
  const [modalIsLoading, setModalIsLoading] = useState<boolean>(false);
  const [fillFromTMLoading, setFillFromTMLoading] = useState<boolean>(false);

  const [newTaggingRuleName, setNewTaggingRuleName] = useState<string>("");
  const [newTaggingRuleRegex, setNewTaggingRuleRegex] = useState<string>("");

  const resetState = (): void => {
    setNewRegexName("");
    setNewRegexRegex("");
    setRegexError(null);
    setNewTaggingRuleName("");
    setNewTaggingRuleRegex("");
  };

  const handleCloseModal = (): void => {
    resetState();
    segmentationSettingsDialogRef.current?.close();
    taggingRulesDialogRef.current?.close();
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleSegmentTextDebounced = useCallback(
    debounce(async (regex: string) => {
      if (!mountedRef.current) {
        return;
      }
      setRegexError(null);
      if (!regex) {
        setIsCheckingRegex(false);
        return;
      }
      try {
        const { error } = await validateRegex(regex).unwrap();
        if (error) {
          setRegexError(error);
        }
      } finally {
        if (mountedRef.current) {
          setIsCheckingRegex(false);
        }
      }
    }, 1000),
    [validateRegex]
  );

  const createNewRegex = useCallback(() => {
    if (!newRegexRegex) {
      return;
    }
    setModalIsLoading(true);
    createTranslationMemorySegmentRegex({
      name: newRegexName,
      regex: newRegexRegex,
    })
      .then((data) => {
        addNewRegex(newRegexRegex, data.id, newRegexName);
        store.dispatch(
          setDjangoToastOpen({
            content: "Regex created successfully",
            appearance: NotificationAppearance.SUCCESS,
          })
        );
        handleCloseModal();
      })
      .catch((error) => {
        if (error.response.data.message.includes("code='unique'")) {
          store.dispatch(
            setDjangoToastOpen({
              content: "Regex with this name already exists",
              appearance: NotificationAppearance.ERROR,
            })
          );
        } else {
          store.dispatch(
            setDjangoToastOpen({
              content: error.response.data.message,
              appearance: NotificationAppearance.ERROR,
            })
          );
        }
      })
      .finally(() => {
        setModalIsLoading(false);
      });
  }, [newRegexName, newRegexRegex, addNewRegex]);

  const fillFromTM = useCallback(async (): Promise<void> => {
    setFillFromTMLoading(true);
    try {
      const batchSize = 10;
      const batches = Math.ceil(segmentationItems.length / batchSize);
      const TMToFill: {
        target_text: string;
        translation_memory_id: number;
        translation_memory_similarity: number;
      }[] = Array(segmentationItems.length).fill({});
      for (let i = 0; i < batches; i++) {
        const batch = segmentationItems.slice(
          i * batchSize,
          (i + 1) * batchSize
        );
        const promises = batch.map((item, index) => {
          return searchTranslationMemory(
            item.source_text,
            sourceLanguage,
            targetLanguage,
            "source_text",
            []
          ).then((results) => {
            if (results.length > 0) {
              if (results[0].similarity >= 1) {
                TMToFill[i * batchSize + index] = {
                  target_text: results[0].target_text,
                  translation_memory_id: results[0].id,
                  translation_memory_similarity: results[0].similarity,
                };
              }
            }
          });
        });
        await Promise.all(promises);
      }
      setSegmentationItems(
        TMToFill.map((item, index) => {
          return {
            index,
            updates: item?.target_text
              ? {
                  target_text: item.target_text,
                  translation_memory_id: item.translation_memory_id,
                  translation_memory_similarity:
                    item.translation_memory_similarity,
                }
              : {},
          };
        })
      );
      const TMToFillCount = TMToFill.filter((item) => item.target_text).length;
      store.dispatch(
        setDjangoToastOpen({
          content: `TM filled successfully for ${TMToFillCount} item(s)`,
          appearance: NotificationAppearance.SUCCESS,
        })
      );
    } finally {
      setFillFromTMLoading(false);
    }
  }, [segmentationItems, setSegmentationItems, sourceLanguage, targetLanguage]);

  if (!allowedToShowAdminControls) {
    return null;
  }

  return (
    <div className="tw-my-2 tw-flex tw-flex-col tw-gap-0.5">
      <h6 className="tw-text-gray-500 !tw-m-0">Admin Panel</h6>
      <div className="tw-grid tw-auto-cols-max tw-grid-flow-col tw-gap-2.5 ">
        <div className="tw-flex tw-flex-col">
          <small className="tw-text-gray-500">Segmentation Settings</small>
          <div className="tw-flex tw-items-center tw-gap-1">
            <Dropdown<number>
              testIdBase="translation-view-regex"
              size="sm"
              options={regexOptions}
              onChange={(value: number) => {
                setRegexId(value);
              }}
              value={regexId}
              placeholder="Segmentation regex"
            />
            <span
              data-testid="translation-view-create-regex-button"
              data-balloon="Create new Segmentation Regex"
              data-balloon-pos="up-left"
            >
              <Button
                icon
                compact
                size="sm"
                onClick={() => {
                  segmentationSettingsDialogRef.current?.showModal();
                }}
                type="button"
              >
                <Icon name="add" />
              </Button>
            </span>
          </div>
        </div>
        <div className="tw-flex tw-flex-col">
          <small className="tw-text-gray-500">Tagging Rules</small>
          <div className="tw-flex tw-items-center tw-flex-1 tw-gap-1">
            <Button
              size="sm"
              compact
              type="button"
              className="tw-w-full"
              onClick={() => {
                taggingRulesDialogRef.current?.showModal();
              }}
              data-testid="translation-view-tagging-rules-settings-button"
            >
              Settings
            </Button>
          </div>
        </div>
        <div className="tw-flex tw-flex-col">
          <small className="tw-text-gray-500 tw-invisible">Fill from TM</small>
          <div
            className="tw-flex tw-items-center tw-flex-1 tw-gap-1"
            data-balloon="Find and automatically fill 100% matches from TM"
            data-balloon-pos="up-left"
          >
            <Button
              size="sm"
              compact
              type="button"
              className="tw-w-full"
              onClick={fillFromTM}
              data-testid="translation-view-fill-from-tm-button"
              disabled={fillFromTMLoading || !segmentationItems.length}
            >
              Fill from TM
            </Button>
          </div>
        </div>
      </div>
      <Modal
        dialogRef={segmentationSettingsDialogRef}
        isLoading={modalIsLoading}
      >
        <Modal.Header divide>
          <h4 data-testid="translation-view-modal-title">
            Create new Segmentation Regex
          </h4>
        </Modal.Header>
        <Modal.Body>
          <div className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-flex-col">
              <label htmlFor="name" className="tw-text-sm">
                Name (optional)
              </label>
              <input
                data-testid="translation-view-regex-name-input"
                type="text"
                id="name"
                className="txu-input"
                placeholder="Sentence Segmenter"
                value={newRegexName}
                onChange={({ target }) => {
                  setNewRegexName(target.value);
                }}
              />
            </div>
            <div className="tw-flex tw-flex-col">
              <label
                htmlFor="regex"
                className="tw-text-sm !tw-flex tw-items-center tw-gap-2"
              >
                Regex * {isCheckingRegex && <Spinner align="left" />}
              </label>
              <input
                data-testid="translation-view-regex-input"
                type="text"
                id="regex"
                className="txu-input"
                placeholder="^[a-zA-Z0-9]+$"
                value={newRegexRegex}
                onChange={({ target }) => {
                  setNewRegexRegex(target.value);
                  setIsCheckingRegex(true);
                  handleSegmentTextDebounced(target.value);
                }}
              />
              {regexError && (
                <small
                  data-testid="translation-view-regex-error"
                  className="tw-text-red-500"
                >
                  {regexError}
                </small>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Actions>
          <Button
            data-testid="translation-view-modal-cancel"
            size="sm"
            onClick={handleCloseModal}
            type="button"
          >
            Cancel
          </Button>
          <Button
            data-testid="translation-view-modal-create"
            size="sm"
            onClick={createNewRegex}
            variant="primary"
            disabled={!newRegexRegex || isCheckingRegex || !!regexError}
            type="button"
          >
            Create
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal dialogRef={taggingRulesDialogRef} isLoading={isFetchingTagRules}>
        <Modal.Header divide>
          <h4 data-testid="translation-view-tagging-rules-modal-title">
            Tagging Rules Settings
          </h4>
        </Modal.Header>
        <Modal.Body>
          <div className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-flex-col">
              <label htmlFor="name" className="tw-text-sm">
                Tagging Rules
              </label>
              <table className="txu-table tw-compact txu-very-compact txu-small-text txu-clickable-rows">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Regex</th>
                    <th>Enabled</th>
                  </tr>
                </thead>
                <tbody>
                  {taggingRules?.rules.map((rule) => (
                    <tr
                      key={rule.id}
                      className="[&:has(input:not(:checked))]:tw-text-gray-400"
                      onClick={() => {
                        setDisabledTaggingRules((prev) => {
                          if (prev.includes(rule.id)) {
                            return [...prev.filter((id) => id != rule.id)];
                          }
                          return [...prev, rule.id];
                        });
                      }}
                    >
                      <td>{rule?.name ?? "Unnamed"}</td>
                      <td>
                        <code>{rule.regex}</code>
                      </td>
                      <td>
                        <ToggleCheckbox
                          disablePointerEvents
                          checked={!disabledTaggingRules.includes(rule.id)}
                          setChecked={NOOP}
                          checkboxId={`tagging-rule-${rule.id}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>
                      <input
                        type="text"
                        placeholder="Name"
                        className="txu-input tw-w-full tw-h-full tw-text-xs"
                        value={newTaggingRuleName}
                        onChange={({ target }) => {
                          setNewTaggingRuleName(target.value);
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        placeholder="Regex"
                        className="txu-input tw-w-full tw-h-full tw-text-xs"
                        value={newTaggingRuleRegex}
                        onChange={({ target }) => {
                          setNewTaggingRuleRegex(target.value);
                        }}
                      />
                    </td>
                    <td>
                      <Button
                        size="sm"
                        compact
                        type="button"
                        onClick={() => {
                          createTaggingRule({
                            name: newTaggingRuleName,
                            regex: newTaggingRuleRegex,
                          }).then(() => {
                            setNewTaggingRuleName("");
                            setNewTaggingRuleRegex("");
                          });
                        }}
                        icon
                      >
                        <Icon name="add" />
                      </Button>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </Modal.Body>
        <Modal.Actions>
          <Button
            data-testid="translation-view-tagging-rules-modal-cancel"
            size="sm"
            onClick={handleCloseModal}
            type="button"
          >
            Close
          </Button>
        </Modal.Actions>
      </Modal>
      <div className="txu-divider !tw-my-1" />
      {fillFromTMLoading && (
        <div className="tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-bottom-0 tw-flex tw-items-center tw-justify-center tw-bg-white/60 tw-z-50">
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
            <h5 className="tw-text-gray-500">Filling from TM...</h5>
            <Spinner />
          </div>
        </div>
      )}
    </div>
  );
};
